import React, { useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export type SnackbarType = 'success' | 'error' | 'info' | 'warning';

interface SnackbarProps {
  message: string;
  type?: SnackbarType;
  isOpen: boolean;
  onClose: () => void;
  duration?: number;
}

export default function Snackbar({ 
  message, 
  type = 'info', 
  isOpen, 
  onClose,
  duration = 3000 
}: SnackbarProps) {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" strokeWidth={2} />;
      case 'error':
        return <XCircle className="w-5 h-5" strokeWidth={2} />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" strokeWidth={2} />;
      case 'info':
      default:
        return <Info className="w-5 h-5" strokeWidth={2} />;
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-orange-400';
      case 'info':
      default:
        return 'text-blue-400';
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-[2000] animate-slide-left">
      <div 
        className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-2xl bg-[#323232] text-white border-0 min-w-[320px]"
      >
        <div className={getIconColor()}>
          {getIcon()}
        </div>
        <span className="text-sm font-normal flex-1">{message}</span>
        <button
          onClick={onClose}
          className="ml-2 hover:bg-white/10 rounded p-1 transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

